import { ReactNode } from "react"

// Mirrors the eCert guide in the backend invitation emails
// (_ecert_requirements.jinja and _ecert_signing_steps.jinja)

const GOOGLE_PLAY_URL =
  "https://play.google.com/store/apps/details?id=com.ecertla.cl.ecertidverify&pli=1"
const APP_STORE_URL = "https://apps.apple.com/cl/app/ecert-verify/id6754342890"
const CLAVE_UNICA_URL = "https://claveunica.gob.cl/"

type Step = {
  title: string
  description: ReactNode
  warning?: { title: string; body: ReactNode }
}

const steps: Step[] = [
  {
    title: "Iniciar sesión con Clave Única",
    description: (
      <>
        Ingresar con RUN y Clave Única, y confirmar el{" "}
        <strong>código de 6 dígitos</strong> que llega al correo. El certificado
        de firma se emite con el nombre registrado en Clave Única.
      </>
    ),
  },
  {
    title: "Validar la identidad con ecert Verify",
    description: (
      <>
        Escanear con el celular el código QR que aparece en pantalla (o tocar
        «Continuar en este dispositivo»). En la app, escanear el QR del{" "}
        <strong>reverso de la cédula</strong> y luego apoyar la cédula en la{" "}
        <strong>parte posterior del celular</strong> para leer el chip: arriba
        en iPhone, al centro en Android. Al ver «Verificación exitosa», tocar
        «Finalizar» y volver al navegador.
      </>
    ),
  },
  {
    title: "Responder las preguntas de seguridad",
    description: (
      <>
        Son <strong>4 preguntas</strong> sobre el historial personal y hay{" "}
        <strong>3 intentos</strong>.
      </>
    ),
    // Running out of attempts is a frequent, costly failure
    warning: {
      title: "Importante: solo hay 3 intentos",
      body: (
        <>
          Las preguntas de seguridad se deben responder correctamente en un
          máximo de <strong>3 intentos</strong>. Responder con calma y revisar
          cada respuesta antes de tocar «Continuar»: si se agotan los intentos,{" "}
          <strong>eCert bloquea la firma por 24 horas</strong>.
        </>
      ),
    },
  },
  {
    title: "Crear la clave del certificado",
    description: (
      <>
        Esta clave <strong>no se puede recuperar</strong>: si se olvida, no será
        posible firmar el documento.
      </>
    ),
  },
  {
    title: "Ingresar el segundo factor de seguridad",
    description: (
      <>
        Llega una <strong>clave de 4 dígitos</strong> al correo. Ingresarla y
        tocar «Firmar».
      </>
    ),
  },
  {
    title: "¡Listo!",
    description: (
      <>
        Cuando todas las personas involucradas hayan firmado, la versión final
        firmada llega por correo.
      </>
    ),
  },
]

const ExternalLink = ({
  href,
  children,
}: {
  href: string
  children: string
}) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className="font-medium underline"
  >
    {children}
  </a>
)

const Section = ({
  title,
  children,
}: {
  title: string
  children: ReactNode
}) => (
  <details className="rounded-lg border p-4">
    <summary className="cursor-pointer font-bold">{title}</summary>
    <div className="mt-3 text-sm">{children}</div>
  </details>
)

const EcertGuide = () => {
  return (
    <div className="flex flex-col gap-3">
      <div className="rounded-lg bg-muted p-4 text-sm">
        <h3 className="mb-2 font-bold">Antes de empezar</h3>
        <p>
          Este documento se firma con{" "}
          <strong>Firma Electrónica Avanzada</strong>. El proceso toma{" "}
          <strong>aproximadamente 10 minutos</strong> y conviene hacerlo con una{" "}
          <strong>buena conexión a internet</strong>. Para completarlo sin
          interrupciones se necesita:
        </p>
        <ul className="mt-2 list-disc space-y-1 pl-5">
          <li>
            <strong>Clave Única</strong> vigente
          </li>
          <li>
            <strong>Cédula de identidad física</strong> y vigente (no vencida)
          </li>
          <li>
            <strong>Celular con NFC</strong> para validar la identidad con la
            app <strong>ecert Verify</strong>, disponible en{" "}
            <ExternalLink href={GOOGLE_PLAY_URL}>Google Play</ExternalLink> y{" "}
            <ExternalLink href={APP_STORE_URL}>App Store</ExternalLink>
          </li>
          <li>
            Acceso al <strong>correo electrónico</strong>, donde llegan los
            códigos de seguridad. El código de Clave Única llega al{" "}
            <strong>correo registrado en Clave Única</strong>: antes de firmar,
            confirmar en el{" "}
            <ExternalLink href={CLAVE_UNICA_URL}>
              Portal ciudadano ClaveÚnica
            </ExternalLink>{" "}
            que sea el que se usa actualmente.
          </li>
        </ul>
      </div>

      <Section title="¿Cómo es el proceso de firma?">
        <ol className="flex flex-col gap-3">
          {steps.map((step, index) => (
            <li key={step.title} className="flex flex-row gap-3">
              <span
                aria-hidden="true"
                className="flex size-6 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground"
              >
                {index + 1}
              </span>
              <div>
                <p className="font-semibold">{step.title}</p>
                <p className="text-muted-foreground">{step.description}</p>
                {step.warning && (
                  <div className="mt-2 rounded-md border-l-4 border-amber-500 bg-amber-100 p-3 text-amber-950 dark:bg-amber-950 dark:text-amber-100">
                    <p className="font-bold">{step.warning.title}</p>
                    <p>{step.warning.body}</p>
                  </div>
                )}
              </div>
            </li>
          ))}
        </ol>
      </Section>

      <Section title="Para tener en cuenta">
        <ul className="list-disc space-y-1 pl-5">
          <li>
            Llegan <strong>dos códigos distintos</strong> al correo: uno de 6
            dígitos (Clave Única) y otro de 4 dígitos (segundo factor). Si no
            llegan, revisar la carpeta de spam.
          </li>
          <li>
            <strong>No volver atrás en el navegador</strong> durante el proceso:
            genera un error. Revisar el documento antes de empezar; si se
            necesita revisarlo de nuevo antes de terminar, hay que solicitar el
            reinicio de la firma.
          </li>
          <li>
            <strong>No cerrar la página de firma</strong> mientras se valida la
            identidad en la app ecert Verify.
          </li>
          <li>
            Si la cédula no se lee, quitar la carcasa del celular y deslizar la
            cédula lentamente por la parte posterior.
          </li>
        </ul>
      </Section>

      <Section title="¿Sin celular con NFC?">
        <p>
          Si el celular no es compatible o la app no logra leer la cédula, en la
          pantalla del código QR se puede tocar{" "}
          <strong>«Tengo problemas con mi teléfono»</strong> para validar la
          identidad con una <strong>transferencia bancaria</strong> de un monto
          pequeño que se devuelve dentro de <strong>24 horas hábiles</strong>.
        </p>
        <ul className="mt-2 list-disc space-y-1 pl-5">
          <li>
            La transferencia debe hacerse desde la{" "}
            <strong>cuenta bancaria de la persona que firma</strong>, nunca
            desde la cuenta de un tercero (familiar, pareja o empresa): si la
            cuenta es de otra persona, la validación falla.
          </li>
          <li>
            El monto depende del banco: en la mayoría está entre{" "}
            <strong>$90 y $300</strong>, pero en algunos puede llegar a{" "}
            <strong>$2.100</strong>, como Santander ($1.000 a $1.100) o
            Consorcio ($2.000 a $2.100).
          </li>
          <li>
            Por el <strong>monto exacto</strong> y dentro de{" "}
            <strong>10 minutos</strong>.
          </li>
          <li>
            No se aceptan depósitos por caja. Nunca se pedirá la clave bancaria.
          </li>
          <li>
            Mantener la ventana abierta hasta ver el mensaje de validación
            exitosa.
          </li>
        </ul>
      </Section>
    </div>
  )
}

export { EcertGuide }
